import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft, Plus, KeyRound, Trash2, Power, Users, Loader2, X, Shield,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";
import LanguageToggle from "../components/LanguageToggle";

export default function DoorStaff() {
    const { t } = useT();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [name, setName] = useState("");
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);
    const [rotateFor, setRotateFor] = useState(null);
    const [rotatePin, setRotatePin] = useState("");

    useEffect(() => {
        if (!localStorage.getItem("sc_admin_token")) {
            navigate("/admin/login", { replace: true });
        }
    }, [navigate]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/door-staff");
            setItems(data);
        } catch {
            toast.error("Failed to load staff");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        if (!name.trim() || !pin.trim()) return;
        setBusy(true);
        try {
            await api.post("/admin/door-staff", { name: name.trim(), pin: pin.trim() });
            toast.success("PIN created");
            setName(""); setPin(""); setShowAdd(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed");
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (s) => {
        try {
            await api.patch(`/admin/door-staff/${s.id}`, { active: !s.active });
            load();
        } catch {
            toast.error("Update failed");
        }
    };

    const remove = async (s) => {
        if (!window.confirm(t("door_admin.confirm_remove"))) return;
        try {
            await api.delete(`/admin/door-staff/${s.id}`);
            toast.success("Removed");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const rotate = async (e) => {
        e.preventDefault();
        if (!rotatePin.trim()) return;
        try {
            await api.patch(`/admin/door-staff/${rotateFor.id}`, { pin: rotatePin.trim() });
            toast.success("PIN rotated");
            setRotateFor(null); setRotatePin("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div className="min-h-[100dvh] text-ink-primary" data-testid="door-staff-page">
            <header className="sticky top-0 z-30 glass-strong border-b border-emerald-line/60">
                <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-ink-secondary hover:text-gold text-xs tracking-[0.22em] uppercase" data-testid="back-admin">
                        <ArrowLeft size={14} /> Admin
                    </Link>
                    <div className="flex items-center gap-3 font-serif text-lg">
                        <Shield size={16} className="text-gold" /> {t("door_admin.title")}
                    </div>
                    <LanguageToggle />
                </div>
                <div className="gold-divider opacity-30" />
            </header>

            <main className="max-w-4xl mx-auto px-6 py-10">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <div className="label-eyebrow">{t("admin.eyebrow")}</div>
                        <h1 className="font-serif font-medium text-3xl md:text-4xl mt-3">{t("door_admin.title")}</h1>
                        <p className="text-ink-secondary mt-3 max-w-xl">{t("door_admin.sub")}</p>
                    </div>
                    <button onClick={() => setShowAdd(true)} data-testid="add-staff"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright">
                        <Plus size={16} /> {t("door_admin.add")}
                    </button>
                </div>

                <div className="rounded-sm border border-emerald-line/60 overflow-hidden bg-bg-secondary/40">
                    {loading && items.length === 0 ? (
                        <div className="p-10 text-center text-ink-muted text-sm">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-10 text-center text-ink-muted text-sm">{t("door_admin.empty")}</div>
                    ) : (
                        <ul className="divide-y divide-emerald-line/60" data-testid="door-staff-list">
                            {items.map((s) => (
                                <li key={s.id} className="px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`staff-${s.id}`}>
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-serif text-lg ${s.active ? "bg-gold/15 text-gold border border-gold/40" : "bg-bg-surface text-ink-muted border border-emerald-line/60"}`}>
                                        {s.name?.[0]?.toUpperCase() || "?"}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div className="font-medium">{s.name}</div>
                                            <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-sm border ${s.active ? "border-emerald-500/40 text-emerald-300" : "border-zinc-600/40 text-zinc-400"}`}>
                                                {s.active ? t("door_admin.active") : t("door_admin.inactive")}
                                            </span>
                                        </div>
                                        <div className="text-xs text-ink-muted mt-1">
                                            {t("door_admin.last_used")}: {s.last_used ? new Date(s.last_used).toLocaleString() : t("door_admin.never")}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setRotateFor(s)} data-testid={`rotate-${s.id}`}
                                            className="px-3 py-1.5 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                                            <KeyRound size={12} /> {t("door_admin.rotate")}
                                        </button>
                                        <button onClick={() => toggleActive(s)} data-testid={`toggle-${s.id}`}
                                            className="px-3 py-1.5 border border-emerald-line/60 text-ink-secondary rounded-sm hover:text-gold hover:border-gold/40 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                                            <Power size={12} /> {s.active ? t("door_admin.deactivate") : t("door_admin.activate")}
                                        </button>
                                        <button onClick={() => remove(s)} data-testid={`remove-${s.id}`}
                                            className="px-3 py-1.5 text-red-400 hover:text-red-300 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                                            <Trash2 size={12} /> {t("door_admin.remove")}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>

            {/* Add modal */}
            {showAdd && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={() => setShowAdd(false)}>
                    <motion.form
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        onSubmit={create} onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md p-8 rounded-sm glass-strong gold-border-anim"
                        data-testid="add-staff-modal"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-serif text-2xl inline-flex items-center gap-2"><Users size={18} className="text-gold" /> {t("door_admin.add")}</h3>
                            <button type="button" onClick={() => setShowAdd(false)} className="text-ink-secondary hover:text-gold"><X size={18} /></button>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <label className="label-eyebrow block mb-2">{t("door_admin.name")}</label>
                                <input value={name} onChange={(e) => setName(e.target.value)} className="input-line" autoFocus data-testid="new-name" />
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-2">{t("door_admin.pin")}</label>
                                <input value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" maxLength={12}
                                    className="input-line tracking-[0.4em] text-xl" data-testid="new-pin" />
                            </div>
                        </div>
                        <button type="submit" disabled={busy || pin.length < 4 || !name.trim()} data-testid="new-staff-submit"
                            className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright disabled:opacity-40">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : t("door_admin.create")}
                        </button>
                    </motion.form>
                </div>
            )}

            {/* Rotate modal */}
            {rotateFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={() => setRotateFor(null)}>
                    <motion.form
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        onSubmit={rotate} onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md p-8 rounded-sm glass-strong gold-border-anim"
                        data-testid="rotate-modal"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-serif text-2xl">{t("door_admin.rotate")} · {rotateFor.name}</h3>
                            <button type="button" onClick={() => setRotateFor(null)} className="text-ink-secondary hover:text-gold"><X size={18} /></button>
                        </div>
                        <label className="label-eyebrow block mb-2">{t("door_admin.new_pin")}</label>
                        <input value={rotatePin} onChange={(e) => setRotatePin(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" maxLength={12}
                            className="input-line tracking-[0.4em] text-xl" autoFocus data-testid="rotate-pin-input" />
                        <button type="submit" disabled={rotatePin.length < 4} data-testid="rotate-submit"
                            className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright disabled:opacity-40">
                            {t("door_admin.rotate")}
                        </button>
                    </motion.form>
                </div>
            )}
        </div>
    );
}
